/**
 * Admin Profit Dashboard
 * Displays profit metrics, trends, and breakdowns by game and payment method
 */

import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'wouter';
import {
  TrendingUp,
  DollarSign,
  TrendingDown,
  Download,
  Calendar,
  Loader2,
} from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

interface MetricCard {
  label: string;
  value: string | number;
  change?: number;
  trend?: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  color: string;
}

export default function AdminProfitDashboard() {
  const [location] = useLocation();
  const [dateFrom, setDateFrom] = useState<Date>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [dateTo, setDateTo] = useState<Date>(new Date());
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const [isExporting, setIsExporting] = useState(false);
  const [isOnPage, setIsOnPage] = useState(false);

  // Check if user is on profit dashboard page
  useEffect(() => {
    setIsOnPage(location === '/admin/profit-dashboard');
  }, [location]);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  // Fetch profit data - only enabled when on profit dashboard page
  const metricsQuery = trpc.admin.profitDashboard.calculateProfitMetrics.useQuery(
    { dateFrom, dateTo },
    { refetchInterval: 60000, enabled: isOnPage }
  );

  const profitByDateQuery = trpc.admin.profitDashboard.getProfitByDate.useQuery(
    { groupBy, dateFrom, dateTo },
    { refetchInterval: 60000, enabled: isOnPage }
  );

  const profitByGameQuery = trpc.admin.profitDashboard.getProfitByGame.useQuery(
    { dateFrom, dateTo, limit: 10 },
    { refetchInterval: 60000, enabled: isOnPage }
  );

  const profitByPaymentQuery = trpc.admin.profitDashboard.getProfitByPaymentMethod.useQuery(
    { dateFrom, dateTo },
    { refetchInterval: 60000, enabled: isOnPage }
  );

  const isLoading = metricsQuery.isLoading || profitByDateQuery.isLoading || profitByGameQuery.isLoading || profitByPaymentQuery.isLoading;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      toast.success('Exporting profit data...');
      // TODO: Implement actual export functionality
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const metrics = metricsQuery.data;
  const profitByDate = profitByDateQuery.data || [];
  const profitByGame = profitByGameQuery.data || [];
  const profitByPayment = profitByPaymentQuery.data || [];

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profit Dashboard</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Analyze revenue, costs, and profit metrics
            </p>
          </div>
          <Button onClick={handleExport} disabled={isExporting} size="sm">
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>

        {/* Date Range Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">From</label>
                <Input
                  type="date"
                  value={dateFrom.toISOString().split('T')[0]}
                  onChange={(e) => setDateFrom(new Date(e.target.value))}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">To</label>
                <Input
                  type="date"
                  value={dateTo.toISOString().split('T')[0]}
                  onChange={(e) => setDateTo(new Date(e.target.value))}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Group By</label>
                <Select value={groupBy} onValueChange={(v) => setGroupBy(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button onClick={() => metricsQuery.refetch()} variant="outline" className="w-full">
                  Update
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">฿{metrics?.totalRevenue?.toLocaleString() || '0'}</div>
              <p className="text-xs text-muted-foreground mt-1">+12% from last period</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Costs</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">฿{metrics?.totalCosts?.toLocaleString() || '0'}</div>
              <p className="text-xs text-muted-foreground mt-1">-5% from last period</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Refunds</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">฿{metrics?.totalRefunds?.toLocaleString() || '0'}</div>
              <p className="text-xs text-muted-foreground mt-1">{((metrics?.totalRefunds || 0) / (metrics?.totalRevenue || 1) * 100).toFixed(1)}% of revenue</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">฿{metrics?.netProfit?.toLocaleString() || '0'}</div>
              <p className="text-xs text-muted-foreground mt-1">{((metrics?.netProfit || 0) / (metrics?.totalRevenue || 1) * 100).toFixed(1)}% margin</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Profit Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Profit Trend</CardTitle>
              <CardDescription>Revenue vs Costs over time</CardDescription>
            </CardHeader>
            <CardContent>
              {profitByDate.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={profitByDate}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" name="Revenue" />
                    <Line type="monotone" dataKey="costs" stroke="#ef4444" name="Costs" />
                    <Line type="monotone" dataKey="profit" stroke="#3b82f6" name="Profit" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Profit by Payment Method */}
          <Card>
            <CardHeader>
              <CardTitle>Profit by Payment Method</CardTitle>
              <CardDescription>Distribution breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              {profitByPayment.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={profitByPayment}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ฿${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="profit"
                    >
                      {profitByPayment.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Games by Profit */}
        <Card>
          <CardHeader>
            <CardTitle>Top Games by Profit</CardTitle>
            <CardDescription>Highest profit-generating games</CardDescription>
          </CardHeader>
          <CardContent>
            {profitByGame.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={profitByGame}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                  <Bar dataKey="costs" fill="#ef4444" name="Costs" />
                  <Bar dataKey="profit" fill="#3b82f6" name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
